import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { MediaToggleDropdown } from '@workspace/ui/components/media-toggle-dropdown'
import { Lightbulb, Mic } from 'lucide-react'
import { useState } from 'react'
import { useMediaDevices } from '../lib/use-media-devices'
import { useVideoPreview } from '../lib/use-video-preview'
import type { MediaSettings } from '../types'
import { AudioVisualizer } from './audio-visualizer'

export interface PreJoinScreenProps {
  userInitial: string
  onJoin: (settings: MediaSettings) => void
  isLoading?: boolean
  error?: string | null
}

export function PreJoinScreen({ userInitial, onJoin, isLoading, error }: PreJoinScreenProps) {
  const [audioEnabled, setAudioEnabled] = useState(true)

  const {
    videoDevices,
    audioDevices,
    selectedVideoDevice,
    selectedAudioDevice,
    setSelectedVideoDevice,
    setSelectedAudioDevice,
  } = useMediaDevices()

  const {
    videoRef,
    isEnabled: videoEnabled,
    setEnabled: setVideoEnabled,
    stopPreview,
  } = useVideoPreview(selectedVideoDevice)

  const handleJoin = () => {
    stopPreview()
    onJoin({
      audioEnabled,
      videoEnabled,
      audioDeviceId: selectedAudioDevice || undefined,
      videoDeviceId: selectedVideoDevice || undefined,
    })
  }

  return (
    <div className="space-y-5">
      <h2 className="font-semibold text-lg text-white">
        Check your audio and video before joining
      </h2>

      {/* Video preview */}
      <div className="aspect-4/3 w-full">
        <div className="relative h-full w-full overflow-hidden rounded-lg bg-card">
          {videoEnabled ? (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="h-full w-full object-cover"
              style={{ transform: 'scaleX(-1)' }}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-secondary">
              <div className="flex size-20 items-center justify-center rounded-full bg-primary">
                <span className="font-semibold text-3xl text-primary-foreground">
                  {userInitial}
                </span>
              </div>
            </div>
          )}

          {/* Media controls */}
          <div className="absolute inset-x-0 bottom-4 flex justify-center gap-3">
            <MediaToggleDropdown
              type="audio"
              enabled={audioEnabled}
              onToggle={() => setAudioEnabled(!audioEnabled)}
              devices={audioDevices}
              selectedDeviceId={selectedAudioDevice}
              onDeviceSelect={setSelectedAudioDevice}
              size="lg"
            />
            <MediaToggleDropdown
              type="video"
              enabled={videoEnabled}
              onToggle={() => setVideoEnabled(!videoEnabled)}
              devices={videoDevices}
              selectedDeviceId={selectedVideoDevice}
              onDeviceSelect={setSelectedVideoDevice}
              size="lg"
            />
          </div>
        </div>
      </div>

      {/* Join CTA card */}
      {error && <p className="text-center text-red-400 text-sm">{error}</p>}
      <div className="rounded-xl border border-border bg-card/30 px-5 py-4">
        <div className="flex items-center justify-between gap-4">
          <div className="h-10 min-w-0 flex-1">
            {audioEnabled ? (
              <AudioVisualizer deviceId={selectedAudioDevice} enabled={audioEnabled} />
            ) : (
              <div className="flex h-full items-center">
                <span className="text-muted-foreground text-xs">Microphone is muted</span>
              </div>
            )}
          </div>
          <CtaButton
            size="lg"
            className="shrink-0"
            onClick={handleJoin}
            disabled={!audioEnabled}
            loading={isLoading}
            icon={<Mic className="size-4" />}
          >
            {isLoading ? 'Joining...' : !audioEnabled ? 'Enable microphone' : 'Join session'}
          </CtaButton>
        </div>
      </div>

      {/* Tip */}
      <div className="flex items-start gap-3 rounded-xl border border-amber-400/20 bg-amber-400/[0.04] px-4 py-3">
        <Lightbulb className="mt-0.5 size-4 shrink-0 text-amber-400" />
        <p className="text-xs leading-relaxed text-muted-foreground">
          <span className="font-medium text-amber-400">Tip:</span> When you're done answering, just
          pause. The AI will pick up that you're ready for the next question.
        </p>
      </div>
    </div>
  )
}
